import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
} from "@mui/material";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

function ProjectDisplay({ initialBoxes = [], onBoxesChange, handleRemove, handleEdit }) {
  // Initialize boxes with a stable "id" for react-beautiful-dnd
  const [boxes, setBoxes] = useState(
    initialBoxes.map((b) => ({ ...b, id: b._id || `temp-${Date.now()}-${Math.random()}` }))
  );

  // Sync parent initialBoxes only once (or whenever needed)
  useEffect(() => {
    setBoxes(
      initialBoxes.map((b) => ({ ...b, id: b._id || `temp-${Date.now()}-${Math.random()}` }))
    );
  }, [initialBoxes]);

  // Notify parent whenever boxes change
  useEffect(() => {
    if (onBoxesChange) onBoxesChange(boxes);
  }, [boxes, onBoxesChange]);

  // Handle drag end to reorder boxes
  const onDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedBoxes = Array.from(boxes);
    const [movedBox] = reorderedBoxes.splice(result.source.index, 1);
    reorderedBoxes.splice(result.destination.index, 0, movedBox);

    setBoxes(reorderedBoxes);
  };

  // Toggle boolean fields in box
  const toggleField = (id, field) => {
    setBoxes((prev) =>
      prev.map((b) => (b._id === id ? { ...b, [field]: !b[field] } : b))
    );
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="boxes">
        {(provided) => (
          <Box
            {...provided.droppableProps}
            ref={provided.innerRef}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              padding: "16px",
            }}
          >
            {boxes?.map((box, index) => (
              <Draggable key={box.id} draggableId={box.id} index={index}>
                {(provided, snapshot) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    sx={{
                      borderRadius: "50px",
                      padding: "16px",
                      backgroundColor: "#DED8D1",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      boxShadow: snapshot.isDragging
                        ? "0 4px 8px rgba(0,0,0,0.2)"
                        : "none",
                    }}
                  >
                    {/* Top Row */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      {/* Left side: project info */}
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: "18px",
                            fontFamily: "Readex Pro",
                            fontWeight: 500,
                          }}
                        >
                          {box.name || "Untitled Project"}
                        </Typography>
                        <Typography
                          sx={{
                            color: "#463C3B",
                            fontFamily: "Readex Pro",
                            fontSize: "14px",
                            fontWeight: 400,
                          }}
                        >
                          {box.customerName || ""},  <span> {box.startMonth || ""}/{box.startYear || ""} –{" "}
                          {box.onGoing
                            ? "Present"
                            : `${box.endMonth || ""}/${box.endYear || ""}`}</span>
                        </Typography>
                    
                      </Box>

                      {/* Right side: actions + switches */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
                        {/* Edit + Delete */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <IconButton
                            aria-label="edit"
                            size="small"
                            onClick={() => handleEdit(box._id, "editnew")}
                          >
                            <ModeEditOutlineOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            aria-label="delete"
                            size="small"
                            onClick={() => handleRemove(box._id)}
                          >
                            <HighlightOffOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        {/* Switches */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={box.highlightInCv || false}
                                onChange={() => toggleField(box.id, "highlightInCv")}
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#463C3B" },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                    backgroundColor: "#463C3B",
                                  },
                                }}
                              />
                            }
                            label="Highlight in CV"
                            sx={{
                              margin: 0,
                              "& .MuiTypography-root": { fontSize: "12px", fontFamily: "Readex Pro", fontWeight: "300" },
                            }}
                          />

                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={!box.excludeInCv}
                                onChange={() => toggleField(box.id, "excludeInCv")}
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#463C3B" },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                    backgroundColor: "#463C3B",
                                  },
                                }}
                              />
                            }
                            label="Include in CV"
                            sx={{
                              margin: 0,
                              "& .MuiTypography-root": { fontSize: "12px", fontFamily: "Readex Pro", fontWeight: "300" },
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </Box>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export default ProjectDisplay;
